import { useMemo, useState, useRef, useEffect } from 'react';
import { SlidersHorizontal, Plus, CheckCircle, Trash } from '@phosphor-icons/react';
import { Trans, t } from '@lingui/macro';
import styled from '@emotion/styled';
import { useTableContext } from 'features/misc/table/providers/TableProvider';
import { Button, Chip, Text } from 'components';
import {
  getNewFilterRow,
  formatForUrl,
  formatForForm,
  useFilters,
  FilterRow as FilterRowType,
} from './transforms';
import { FilterRow } from './FilterRow';
import { Box, Popover, useTheme } from '@mui/material';
import { useUser } from 'features/auth';
import { useModule } from 'features/accessControl';
import { checkRole } from 'features/accessControl/utils';
import { Switch } from 'components/inputs/Switch';
import { getIsFilterChange } from '../../utils';

export interface FilterProps {
  title?: React.ReactNode;
}

export const Filter = ({ title }: FilterProps) => {
  const theme = useTheme();
  const hasComplianceModuleAccess = useModule('Compliance');
  const [filterRows, setFilterRows] = useState<Record<string, FilterRowType>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { table, config } = useTableContext();
  const { data: userData } = useUser();
  const filterState: Record<string, string> = table.options.meta?.tableState || {};
  const filterStateChange = table.options.meta?.onTableStateChange || null;
  const isComplianceModuleActive =
    hasComplianceModuleAccess &&
    (checkRole('Compliance Admin', userData?.loginContext?.roleNames) ||
      checkRole('Compliance User', userData?.loginContext?.roleNames));

  const filtersDefaults = useFilters(
    table.options.meta?.tableFilters?.(
      userData?.loginContext?.designerOrganizationId,
      isComplianceModuleActive
    ) || []
  ).filter(
    f =>
      isComplianceModuleActive ||
      ![
        'certificateValidFromTo',
        'certificateCategory',
        'certificateType',
        'certificateStates',
        'institutes',
      ].includes(f.id)
  );

  const filters = filtersDefaults?.filter(item => !item.bottomSwitch);

  const bottomSwitch = filtersDefaults?.find(item => item.bottomSwitch);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const showSave = false; // not relevant for current features, not sure of exact logic yet

  const setFilterDefault = () => {
    const newRow = getNewFilterRow();
    const hasBottom = filtersDefaults.find(item => item?.bottomSwitch);
    const bottomSwitchFilter = {
      ...getNewFilterRow(),
      checked: false,
      column: hasBottom?.id as string,
    };
    setFilterRows(prev => ({
      [newRow.id]: newRow,
      ...(hasBottom && bottomSwitchFilter.id
        ? { [bottomSwitchFilter.id]: bottomSwitchFilter }
        : {}),
    }));
    submit(
      hasBottom && bottomSwitchFilter.id ? { [bottomSwitchFilter.id]: bottomSwitchFilter } : {},
      true
    );
  };

  const resetRowInPlace = (rowId: string | number) => {
    const resetRow = { ...getNewFilterRow(), id: rowId };
    const hasBottom = filtersDefaults.find(item => item?.bottomSwitch);
    setFilterRows(prev => {
      const bottomEntries = Object.fromEntries(
        Object.entries(prev).filter(([, v]) => v?.column === hasBottom?.id)
      );
      return { [rowId]: resetRow, ...bottomEntries };
    });
    const bottomSwitchEntry = hasBottom
      ? Object.values(filterRows).find(r => r?.column === hasBottom.id)
      : undefined;
    submit(bottomSwitchEntry ? { [bottomSwitchEntry.id]: bottomSwitchEntry } : {}, true);
  };

  const submit = (rows?: Record<string, FilterRowType>, rowsOnly?: boolean) => {
    const changedValues = formatForUrl(
      Object.values({ ...(filterRows && !rowsOnly ? filterRows : {}), ...(rows ?? {}) }),
      filtersDefaults,
      table?.options?.meta?.tableState
    );

    const isFilterChange = getIsFilterChange(
      changedValues,
      table?.options?.meta?.tableState,
      filtersDefaults
    );

    if (filterStateChange && isFilterChange) {
      filterStateChange({
        ...changedValues,
        page: null,
        cursor: null,
      });
    }
  };

  const onOpen = () => {
    const filterValues = formatForForm(filterState, filtersDefaults);
    const bottomId = bottomSwitch?.id;

    const filterValuesNew = [...filterValues];

    const hasBottom = filterValuesNew.some(item => item.column === bottomId);

    if (filterValues.length === 0 || (hasBottom && filterValues.length === 1)) {
      filterValuesNew.push(getNewFilterRow());
    }

    if (!hasBottom && bottomId) {
      filterValuesNew.push({
        ...getNewFilterRow(),
        checked: false,
        column: bottomId,
      });
    }

    const rowsFilter = filterValuesNew.reduce(
      (acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      },
      {} as Record<string, (typeof filterValuesNew)[number]>
    );

    setFilterRows(rowsFilter);
    config?.event?.('filterClicked', null);
  };

  const handleClose = () => {
    submit();
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onOpen();
  };

  const isFilterOpen = Boolean(anchorEl);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    const isEnter = event.key === 'Enter';
    const isEscape = event.key === 'Escape';
    if ((isEnter || isEscape) && isFilterOpen) {
      handleClose();
    }
  };

  const id = isFilterOpen ? 'table-filter-popover' : undefined;
  const filterRowsNew = Object.values(filterRows)?.filter(
    item => item?.column !== bottomSwitch?.id
  );
  const isAnyColumn = filterRowsNew?.some(item => item?.column);

  useEffect(() => {
    if (scrollContainerRef.current && filterRowsNew.length > 0) {
      const container = scrollContainerRef.current;
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }, 0);
    }
  }, [filterRowsNew.length]);

  const bottomSwitchValue = Object.values(filterRows)?.find(
    item => item?.column === bottomSwitch?.id
  );
  const filterCount = formatForForm(filterState, filters).length;
  const btnBg = useMemo(() => {
    return (
      isFilterOpen ||
      filterCount > 0 ||
      formatForForm(filterState, filtersDefaults)?.find(item => item?.column === bottomSwitch?.id)
        ?.checked
    );
  }, [isFilterOpen, formatForForm(filterState, filters), filterCount]);

  const filterChipLabel = formatForForm(filterState, filtersDefaults)?.find(
    item => item?.column === bottomSwitch?.id
  )?.checked
    ? formatForForm(filterState, filters).length + 1
    : formatForForm(filterState, filters).length;

  return (
    <>
      <Button
        sx={{
          ...(btnBg
            ? {
                background: theme.palette.bg.brandPrimary,
                color: theme.palette.text.brandSecondary,
              }
            : {}),
        }}
        startIcon={
          <SlidersHorizontal
            data-testid={`table_${config.tableId}_filter_icon`}
            color="currentColor"
            fill="currentColor"
            size={20}
          />
        }
        variant="tertiary"
        size="lg"
        onClick={handleClick}
        aria-describedby={id}
        data-testid={`table_${config.tableId}_filter`}
      >
        <Trans>Filter</Trans>{' '}
        <Chip
          data-testid={`table_${config.tableId}_filter_count`}
          isBackgroundFilled
          size="sm"
          isTooltip={false}
          color="grey"
          label={filterChipLabel}
        />
      </Button>
      <StyledPopover
        id={id}
        open={isFilterOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        onKeyDown={handleKeyPress}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          ...(isAnyColumn
            ? {
                '.MuiPopover-paper': {
                  width: '100%',
                },
              }
            : {}),
        }}
        disableRestoreFocus
        data-testid={`table_${config.tableId}_filter_popover`}
      >
        <Column>
          <Row>
            <Text
              variant="labelMd"
              weight="semiBold"
              data-testid={`table_${config.tableId}_filter_popover_title`}
            >
              {title}
            </Text>
            {showSave ? (
              <Button
                variant="link"
                size="sm"
                startIcon={<CheckCircle size={16} color="currentColor" fill="currentColor" />}
                onClick={() => submit()}
                data-testid={`table_${config.tableId}_filter_popover_save_to_view`}
              >
                <Text variant="labelSm" weight="regular">
                  <Trans>Save changes to View</Trans>
                </Text>
              </Button>
            ) : null}
          </Row>
          <form
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Column
              ref={scrollContainerRef}
              style={{ maxHeight: 'calc(100vh - 500px)', overflow: 'auto', minHeight: '60px' }}
            >
              {filterRowsNew.map((row, index) => (
                <FilterRow
                  tableId={config.tableId}
                  key={row.id}
                  data={row}
                  filters={filters}
                  filterRows={filterRowsNew}
                  onRemove={() => {
                    if (filterRowsNew?.length === 1) {
                      resetRowInPlace(row.id);
                    } else {
                      const updatedState = { ...filterRows };
                      delete updatedState?.[row.id];
                      setFilterRows(updatedState);
                      submit(updatedState, true);
                    }
                  }}
                  selected={filterRowsNew}
                  onChange={(newValues, doUpdate) => {
                    if (newValues.column === '' && doUpdate) {
                      if (filterRowsNew?.length === 1) {
                        resetRowInPlace(newValues.id);
                      } else {
                        const updatedState = { ...filterRows };
                        delete updatedState?.[newValues.id];
                        setFilterRows(updatedState);
                        submit(updatedState, true);
                      }
                      return;
                    }

                    setFilterRows(prev => {
                      return { ...prev, [newValues.id]: newValues };
                    });
                    if (doUpdate) {
                      submit({ [newValues.id]: newValues });
                    }
                  }}
                />
              ))}
            </Column>
          </form>
          <RowRight>
            {bottomSwitch && bottomSwitchValue?.id && (
              <Box
                sx={{
                  marginRight: 'auto',
                }}
              >
                <Switch
                  size="small"
                  labelColor={theme.palette.text.primary}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                  data-testid={`table_${config.tableId}_filter_popover_bottom_switch`}
                  checked={bottomSwitchValue?.checked}
                  onChange={(_, checked) => {
                    setFilterRows(prev => {
                      return {
                        ...prev,
                        [bottomSwitchValue.id]: { ...(bottomSwitchValue ?? {}), checked },
                      };
                    });
                    submit({ [bottomSwitchValue.id]: { ...(bottomSwitchValue ?? {}), checked } });
                  }}
                  label={bottomSwitch?.label}
                />
              </Box>
            )}
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                setFilterDefault();
                // resize event will force the popover to reposition within window
                window.dispatchEvent(new Event('resize'));
              }}
              startIcon={
                <Trash
                  size={20}
                  color="inherit"
                  data-testid={`table_${config.tableId}_filter_popover_clear_all_filters_icon`}
                />
              }
              data-testid={`table_${config.tableId}_filter_popover_clear_all_filters`}
            >
              <Text variant="labelSm" weight="regular">
                <Trans>Clear all filters</Trans>
              </Text>
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                const newRow = getNewFilterRow();
                setFilterRows(prev => ({ ...prev, [newRow.id]: newRow }));
                // resize event will force the popover to reposition within window
                window.dispatchEvent(new Event('resize'));
              }}
              startIcon={
                <Plus
                  size={20}
                  color="inherit"
                  data-testid={`table_${config.tableId}_filter_popover_add_filter_icon`}
                />
              }
              data-testid={`table_${config.tableId}_filter_popover_add_filter`}
            >
              <Text variant="labelSm" weight="regular">
                <Trans>Add a filter</Trans>
              </Text>
            </Button>
          </RowRight>
        </Column>
      </StyledPopover>
    </>
  );
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  justify-content: space-between;
`;

const RowRight = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
`;

const Column = styled.div`
  flex-direction: column;
  display: flex;
  gap: 24px;
`;

const StyledPopover = styled(Popover)`
  .MuiPopover-paper {
    background-color: ${p => p.theme?.palette?.bg?.primary};
    max-width: 975px;
    max-height: calc(100vh - 200px);
    min-height: 215px;
    border-radius: 8px;
    border: 0.5px solid ${p => p.theme?.palette?.border?.primarySubtle};
    overflow: auto;
    padding: 24px;
    margin-top: 5px;
    box-shadow:
      0px 12px 16px -4px rgba(16, 24, 40, 0.08),
      0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  }
`;
